/**
 * @file ReservationsListView.tsx
 * @description Operational view for guest table bookings, party seatings, and shift reservations.
 */

import React, { useState } from 'react';
import type { WaiterReservation, WaiterReservationStatus } from '../../../types/reservation.types';
import {
  Calendar,
  Users,
  Phone,
  Clock,
  CheckCircle2,
  XCircle,
  Search,
  Utensils,
  AlertTriangle,
} from 'lucide-react';

interface ReservationsListViewProps {
  reservations: WaiterReservation[];
  isLoading: boolean;
  onUpdateStatus: (id: string, status: WaiterReservationStatus, reason?: string) => void;
  onTakeOrder?: (tableNumber: string) => void;
}

type ReservationTabFilter = 'upcoming' | 'seated' | 'all';

export const ReservationsListView: React.FC<ReservationsListViewProps> = ({
  reservations,
  isLoading,
  onUpdateStatus,
  onTakeOrder,
}) => {
  const [activeTab, setActiveTab] = useState<ReservationTabFilter>('upcoming');
  const [searchQuery, setSearchQuery] = useState('');
  const [cancelCandidate, setCancelCandidate] = useState<WaiterReservation | null>(null);

  // Derive counts for filter tabs
  const upcomingCount = reservations.filter(
    r => r.status === 'confirmed' || r.status === 'pending'
  ).length;
  const seatedCount = reservations.filter(r => r.status === 'seated').length;
  const allCount = reservations.length;

  const filtered = reservations.filter(res => {
    // 1. Status Tab filter
    if (activeTab === 'upcoming' && res.status !== 'confirmed' && res.status !== 'pending') {
      return false;
    }
    if (activeTab === 'seated' && res.status !== 'seated') {
      return false;
    }

    // 2. Search query filter
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    return (
      res.customer_name.toLowerCase().includes(q) ||
      (res.customer_phone && res.customer_phone.includes(q)) ||
      (res.table_number && res.table_number.toLowerCase().includes(q))
    );
  });

  const handleConfirmCancel = () => {
    if (!cancelCandidate) return;
    onUpdateStatus(cancelCandidate.id, 'cancelled', 'Cancelled by floor staff');
    setCancelCandidate(null);
  };

  return (
    <div className="space-y-4">
      {/* 1. Header with Status Tabs and Live Search */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-[#121212] p-3 rounded-2xl border border-white/10">
        {/* Status Filter Tabs */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0 scrollbar-none">
          {/* Upcoming (Default) */}
          <button
            type="button"
            onClick={() => setActiveTab('upcoming')}
            className={`min-h-[40px] px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shrink-0 active:scale-[0.98] ${
              activeTab === 'upcoming'
                ? 'bg-[#ff4f00] text-white shadow-lg shadow-[#ff4f00]/30 font-black'
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            <Clock className="w-3.5 h-3.5" />
            <span>Upcoming</span>
            <span className="text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold bg-black/40 text-white">
              {upcomingCount}
            </span>
          </button>

          {/* Seated */}
          <button
            type="button"
            onClick={() => setActiveTab('seated')}
            className={`min-h-[40px] px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shrink-0 active:scale-[0.98] ${
              activeTab === 'seated'
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30 font-black'
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            <Users className="w-3.5 h-3.5" />
            <span>Seated</span>
            <span className="text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold bg-white/10 text-zinc-300">
              {seatedCount}
            </span>
          </button>

          {/* All */}
          <button
            type="button"
            onClick={() => setActiveTab('all')}
            className={`min-h-[40px] px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shrink-0 active:scale-[0.98] ${
              activeTab === 'all'
                ? 'bg-white/15 text-white border border-white/20 font-black'
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            <span>All Shift</span>
            <span className="text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold bg-white/10 text-zinc-300">
              {allCount}
            </span>
          </button>
        </div>

        {/* Live Search Input */}
        <div className="relative flex-1 md:w-72">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search guest, phone, table..."
            className="w-full pl-10 pr-3 py-2 min-h-[40px] rounded-xl bg-[#0a0a0a] border border-white/10 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[#ff4f00]"
          />
        </div>
      </div>

      {/* 2. Loading and Empty States */}
      {isLoading && reservations.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-zinc-500 text-sm">
          Loading reservations...
        </div>
      ) : filtered.length === 0 ? (
        <div className="h-64 flex flex-col items-center justify-center text-zinc-500 text-center">
          <Calendar className="w-10 h-10 mb-2 text-zinc-600" />
          <p className="text-sm font-bold text-zinc-300">No Reservations Found</p>
          <p className="text-xs text-zinc-500 mt-0.5">
            {activeTab === 'upcoming'
              ? 'No upcoming guests arriving for this shift window.'
              : activeTab === 'seated'
              ? 'No currently seated parties in this view.'
              : 'There are no matching reservations for this venue.'}
          </p>
        </div>
      ) : (
        /* 3. Responsive Reservations Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(res => {
            const startDate = new Date(res.start_time);
            const timeStr = !isNaN(startDate.getTime())
              ? startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
              : 'TBD';
            const dateStr = !isNaN(startDate.getTime())
              ? startDate.toLocaleDateString([], { month: 'short', day: 'numeric' })
              : 'Today';

            const isUpcoming = res.status === 'confirmed' || res.status === 'pending';
            const isSeated = res.status === 'seated';
            const isCancelled = res.status === 'cancelled';

            return (
              <div
                key={res.id}
                className={`p-5 rounded-3xl bg-[#121212] border flex flex-col justify-between transition-all ${
                  isUpcoming
                    ? 'border-[#ff4f00]/40 shadow-lg shadow-[#ff4f00]/10 ring-1 ring-[#ff4f00]/20'
                    : isSeated
                    ? 'border-blue-500/40 shadow-lg shadow-blue-950/20'
                    : isCancelled
                    ? 'border-rose-950/60 opacity-60'
                    : 'border-white/10'
                }`}
              >
                <div>
                  {/* Top Details Header */}
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div>
                      <h4 className="text-base font-black text-white tracking-tight">
                        {res.customer_name}
                      </h4>
                      <div className="flex items-center gap-2 mt-1 text-xs text-zinc-400">
                        <Users className="w-3.5 h-3.5 text-zinc-500" />
                        <span>{res.party_size} Guests</span>
                        {res.table_number && (
                          <>
                            <span>•</span>
                            <span className="font-bold text-zinc-200">
                              Table {res.table_number}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Status Badge */}
                    <span
                      className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full ${
                        isSeated
                          ? 'bg-blue-950 text-blue-300 border border-blue-800/60'
                          : isUpcoming
                          ? 'bg-emerald-950 text-emerald-300 border border-emerald-800/60'
                          : isCancelled
                          ? 'bg-rose-950 text-rose-300 border border-rose-800/60'
                          : 'bg-zinc-800 text-zinc-400'
                      }`}
                    >
                      {res.status}
                    </span>
                  </div>

                  {/* Scheduled Date, Time, Phone, and Dietary Notes */}
                  <div className="space-y-1.5 text-xs text-zinc-400 border-t border-white/5 pt-3">
                    <div className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-[#ff4f00]" />
                      <span className="font-semibold text-zinc-200">
                        {dateStr} at {timeStr}
                      </span>
                    </div>

                    {res.customer_phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-3.5 h-3.5 text-zinc-500" />
                        <a
                          href={`tel:${res.customer_phone}`}
                          className="text-cyan-400 hover:underline"
                        >
                          {res.customer_phone}
                        </a>
                      </div>
                    )}

                    {res.special_requests && (
                      <p className="text-[11px] text-amber-300/90 italic pt-1">
                        Note: {res.special_requests}
                      </p>
                    )}
                  </div>
                </div>

                {/* Bottom Actions based on status */}
                {isUpcoming && (
                  <div className="flex items-center gap-2 pt-4 border-t border-white/5 mt-4">
                    {/* Primary Seat Guests Action */}
                    <button
                      type="button"
                      onClick={() => onUpdateStatus(res.id, 'seated')}
                      className="flex-1 py-3 px-3 min-h-[44px] rounded-xl bg-blue-600 hover:bg-blue-500 active:scale-[0.98] text-white font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 cursor-pointer transition-all shadow-md shadow-blue-950/30"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Seat Guests</span>
                    </button>

                    {/* Safe Cancel Action */}
                    <button
                      type="button"
                      onClick={() => setCancelCandidate(res)}
                      className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl bg-white/5 hover:bg-rose-950/50 hover:text-rose-300 active:scale-[0.98] text-zinc-400 font-bold text-xs cursor-pointer transition-all border border-transparent hover:border-rose-900"
                      title="Cancel reservation"
                      aria-label="Cancel reservation"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Seated: Provide Take Order action if table assigned */}
                {isSeated && res.table_number && onTakeOrder && (
                  <div className="pt-4 border-t border-white/5 mt-4">
                    <button
                      type="button"
                      onClick={() => onTakeOrder(res.table_number!)}
                      className="w-full py-2.5 px-3 min-h-[42px] rounded-xl bg-white/5 hover:bg-white/10 active:scale-[0.98] text-zinc-200 hover:text-white font-bold text-xs flex items-center justify-center gap-2 transition-all cursor-pointer border border-white/10"
                    >
                      <Utensils className="w-3.5 h-3.5 text-[#ff4f00]" />
                      <span>Take Order for Table {res.table_number}</span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 4. Safe Cancellation Confirmation Modal */}
      {cancelCandidate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-sm bg-[#121212] border border-white/10 rounded-3xl p-5 shadow-2xl space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-rose-950/60 border border-rose-800/60 text-rose-400 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-6 h-6" />
            </div>

            <div className="text-center space-y-1">
              <h3 className="text-base font-black text-white">Cancel Reservation?</h3>
              <p className="text-xs text-zinc-400">
                Are you sure you want to cancel the booking for{' '}
                <span className="font-bold text-white">{cancelCandidate.customer_name}</span> ({cancelCandidate.party_size} guests)?
              </p>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => setCancelCandidate(null)}
                className="flex-1 py-3 min-h-[44px] rounded-xl bg-white/10 hover:bg-white/15 text-zinc-300 font-bold text-xs uppercase tracking-wider cursor-pointer transition-colors"
              >
                Keep Booking
              </button>
              <button
                type="button"
                onClick={handleConfirmCancel}
                className="flex-1 py-3 min-h-[44px] rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs uppercase tracking-wider cursor-pointer transition-colors shadow-lg shadow-rose-950/50"
              >
                Confirm Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
